import {Box, Flex, showMessage, Text} from 'exsportia-components'
import {FC} from 'react'
import styled from 'styled-components'

import {useVenueById} from '../../api/venue/hooks'
import {TranslatorScope} from '../../locale/types'
import {ExsportiaIcon} from '../../ui/components/exsportia-icon/exsportia-icon'
import {Loader} from '../../ui/components/loader/loader'
import {useLocale} from '../../utils/i18n'
import {ChannelLogo} from './channel-logo'
import {channelTexts, platformNameKey, VenueChannel, venueChannels} from './contact-links'

type ChannelSource = Parameters<typeof venueChannels>[0]

export type BookingContext = {reference?: string; venueName?: string}

const SUPPORT_WHATSAPP = 'https://wa.me/66948951432'

const supportChannel = (name: string): VenueChannel => ({
  type: 'WHATSAPP',
  text: name,
  value: 'WhatsApp +66 94 895 1432',
  href: SUPPORT_WHATSAPP,
  label: '+66 94 895 1432',
})

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 4px;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 16px;
  }
`

const RowLink = styled.a`
  flex: 1;
  min-width: 0;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  &:hover {
    background: #f3f4f6;
  }
`

const ellipsis = {overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'} as const

const SingleColumn = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 4px;
`

const Caption = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`

const ChannelRow: FC<{channel: VenueChannel}> = ({channel}) => {
  const {l} = useLocale()
  const canCopy = channel.type === 'PHONE' || channel.type === 'EMAIL'
  const copy = () => {
    navigator.clipboard
      .writeText(channel.value)
      .then(() => showMessage(l('copied'), 'success'))
      .catch((e) => console.log(e))
  }
  const known = l(platformNameKey(channel.type) as TranslatorScope)
  const platformName = known.startsWith('[')
    ? channel.type.charAt(0) + channel.type.slice(1).toLowerCase()
    : known
  const {title, subtitle} = channelTexts(channel, platformName)
  const content = (
    <div style={{display: 'flex', alignItems: 'center', gap: 12, padding: '4px 4px', minHeight: 44}}>
      <ChannelLogo type={channel.type} />
      <div style={{minWidth: 0, flex: 1}}>
        <div style={{fontSize: 14, fontWeight: 700, ...ellipsis}}>{title}</div>
        <div style={{fontSize: 13, fontWeight: 600, color: '#6B7280', ...ellipsis}}>{subtitle}</div>
      </div>
    </div>
  )
  return (
    <Flex
      alignItems='center'
      style={{minWidth: 0}}
    >
      {channel.href ? (
        <RowLink
          href={channel.href}
          target='_blank'
          rel='noopener noreferrer'
        >
          {content}
        </RowLink>
      ) : (
        <div style={{flex: 1, minWidth: 0}}>{content}</div>
      )}
      {canCopy && (
        <button
          type='button'
          onClick={copy}
          aria-label={l('copy')}
          title={l('copy')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            width: 32,
            height: 32,
            flexShrink: 0,
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ExsportiaIcon
            icon='copy'
            w={16}
            h={16}
          />
        </button>
      )}
    </Flex>
  )
}

const ChannelList: FC<{channels: VenueChannel[]; single?: boolean}> = ({channels, single}) => {
  const Wrapper = single ? SingleColumn : Grid
  return (
    <Wrapper>
      {channels.map((channel, index) => (
        <ChannelRow
          key={`${index}_${channel.type}_${channel.value}`}
          channel={channel}
        />
      ))}
    </Wrapper>
  )
}

export const ContactsList: FC<{data?: ChannelSource; isLoading?: boolean; booking?: BookingContext}> = ({
  data,
  isLoading,
  booking,
}) => {
  const {l} = useLocale()
  const prefillText = booking?.reference
    ? l('contact_prefill_booking', {ref: booking.reference, venue: booking.venueName || ''})
    : undefined
  const channels = venueChannels(data, {prefillText})
  if (isLoading) {
    return (
      <Flex
        justifyContent='center'
        py='16px'
      >
        <Loader />
      </Flex>
    )
  }
  if (!channels.length) {
    return (
      <Box py='8px'>
        <Text additionalStyles={['subText']}>{l('venue_no_contacts')}</Text>
      </Box>
    )
  }
  return <ChannelList channels={channels} />
}

export const ContactUsPage: FC<{venueId: string; booking?: BookingContext}> = ({venueId, booking}) => {
  const {data: venueData, isLoading} = useVenueById(venueId)
  return (
    <Box mb='16px'>
      <ContactsList
        data={venueData}
        isLoading={isLoading}
        booking={booking}
      />
    </Box>
  )
}

export const GetHelpSheet: FC<{venueId?: string; booking?: BookingContext}> = ({venueId, booking}) => {
  const {l} = useLocale()
  const {data: venueData, isLoading} = useVenueById(venueId)
  return (
    <Box mb='16px'>
      <Text settings={{fontWeight: '600'}}>{l('get_help_title')}</Text>
      <Box mt='12px'>
        <Caption>{l('help_with_booking_or_venue')}</Caption>
        <ContactsList
          data={venueData}
          isLoading={isLoading}
          booking={booking}
        />
      </Box>
      <Box
        mt='16px'
        pt='16px'
        style={{borderTop: '1px solid #E5E7EB'}}
      >
        <Caption>{l('help_technical_problem')}</Caption>
        <ChannelList
          single
          channels={[supportChannel(l('help_support_name'))]}
        />
      </Box>
    </Box>
  )
}
